import type { ScaleLinear, ScaleTime } from 'd3-scale';
import { useRef } from 'react';

import { ganttChartHeaderCSS } from './DataTableV2GanttChartHeader.sty.js';
import { useMinMaxFromConfig } from './hooks/useMinMaxFromConfig.js';
import { useScaleAndFormatter } from './hooks/useScaleAndFormatter.js';
import { TimeTableHeaderAxis } from '../../../../charts/core/components/axes/components/TimeTableHeaderAxis.js';
import { ValueTableHeaderAxis } from '../../../../charts/core/components/axes/components/ValueTableHeaderAxis.js';
import type { TickValueFormatter } from '../../../../charts/core/types/tick-value.js';
import { useContainerSize } from '../../../../core/hooks/useContainerSize.js';
import { DataTableV2DefaultCell } from '../../components/DataTableV2DefaultCell.js';
import type { DataTableV2DisplayColumnDef } from '../../public.api.js';
import { type DataTableV2GanttColumnConfig } from '../ColumnTypes/ColumnTypes.js';

export const DataTableV2GanttChartHeader = <TData, TValue>({
  columnDef,
}: {
  columnDef: DataTableV2DisplayColumnDef<TData, TValue>;
}) => {
  const sizeRef = useRef<HTMLDivElement>(null);
  const { width, height } = useContainerSize(sizeRef);
  const config = (columnDef.config || {}) as DataTableV2GanttColumnConfig;
  const { xAxisType = 'numerical' } = config;
  const { min, max } = useMinMaxFromConfig(columnDef.id);
  const { scale, formatter } = useScaleAndFormatter(width, config, min, max);

  return (
    <DataTableV2DefaultCell
      ref={sizeRef}
      className={ganttChartHeaderCSS}
      data-sizeref={true}
    >
      {xAxisType === 'time' ? (
        <TimeTableHeaderAxis
          scale={scale as ScaleTime<number, number>}
          containerHeight={height}
          containerWidth={width}
          min={min}
          max={max}
        />
      ) : (
        <ValueTableHeaderAxis
          scale={scale as ScaleLinear<number, number>}
          formatter={formatter as TickValueFormatter}
          containerHeight={height}
          containerWidth={width}
          min={min}
          max={max}
        />
      )}
    </DataTableV2DefaultCell>
  );
};
